import { IInvestorsListData } from "@redbelly-tokeniser/mystake-common-sdk";
import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { Skeleton } from "../../commons/Styled";
import { Button, DataTable, Icon } from "../../../designSystem";
import { personNameToString } from "../../utility/formatter";
// eslint-disable-next-line import/no-unresolved, import/extensions
import { Order } from "../../../types";

interface HeadCell {
  id: string;
  numeric: boolean;
  label: string;
  align?: "left" | "right";
}

export enum InvestorsTableColumns {
  NAME = "legalEntityName",
  CONTACT_NAME = "contact_name",
  SHARES = "sharesValue",
  OPTIONS = "optionsCount",
  NOTES = "notesValue",
  IDENTITY_REGISTRY = "identityRegistry",
}

export type InvestorListingState = {
  known: boolean;
  registered: boolean;
};

interface IInvestorsTableData {
  investorsList: IInvestorsListData["investors"] | undefined;
  sortOrder: Order;
  sortBy: InvestorsTableColumns;
  loading: boolean;
  setSortOrder: (e: Order) => void;
  setSortBy: (e: InvestorsTableColumns) => void;
  showIdentityRegistryActions?: boolean;
  listingByShareholderId?: Record<string, InvestorListingState>;
  pendingRowId?: string | null;
  identityActionsReady?: boolean;
  onRegisterIdentity?: (investor: IInvestorsListData["investors"][0]) => void;
  onUnregisterIdentity?: (investor: IInvestorsListData["investors"][0]) => void;
}

interface InvestorRow {
  key: string;
  legalEntityName: string;
  contactName: string;
  sharesValue: React.ReactNode;
  optionsCount: React.ReactNode;
  notesValue: React.ReactNode;
  identityRegistry: React.ReactNode;
  navUrl: string;
}

const headCells: HeadCell[] = [
  { id: InvestorsTableColumns.NAME, numeric: false, label: "Investor name" },
  { id: InvestorsTableColumns.CONTACT_NAME, numeric: false, label: "Contact name" },
  { id: InvestorsTableColumns.SHARES, numeric: true, label: "Shares", align: "right" },
  { id: InvestorsTableColumns.OPTIONS, numeric: true, label: "Options", align: "right" },
  { id: InvestorsTableColumns.NOTES, numeric: true, label: "Notes", align: "right" },
];

export const InvestorsTable: React.FC<IInvestorsTableData> = ({
  investorsList,
  sortBy,
  setSortBy,
  sortOrder,
  loading,
  setSortOrder,
  showIdentityRegistryActions = false,
  listingByShareholderId = {},
  pendingRowId = null,
  identityActionsReady = true,
  onRegisterIdentity,
  onUnregisterIdentity,
}) => {
  const handleRequestSort = (property: InvestorsTableColumns) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  };

  const history = useHistory();
  const { companyId } = useParams() as { companyId: string };

  const sortHeader = (headCell: HeadCell) => (
    <button
      type="button"
      onClick={() => handleRequestSort(headCell.id as InvestorsTableColumns)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        margin: 0,
        padding: 0,
        border: "none",
        background: "none",
        font: "inherit",
        color: "inherit",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {headCell.label}
      {sortBy === headCell.id ? (
        <Icon name="chevronDown" size={14} style={{ transform: sortOrder === "asc" ? "rotate(180deg)" : undefined }} />
      ) : null}
    </button>
  );

  const visibleHeadCells = [
    ...headCells,
    ...(showIdentityRegistryActions
      ? [{ id: InvestorsTableColumns.IDENTITY_REGISTRY, numeric: false, label: "", align: "right" as const }]
      : []),
  ];

  const columns = visibleHeadCells.map((headCell) => ({
    key:
      headCell.id === InvestorsTableColumns.NAME
        ? "legalEntityName"
        : headCell.id === InvestorsTableColumns.CONTACT_NAME
          ? "contactName"
          : headCell.id,
    label: headCell.id === InvestorsTableColumns.IDENTITY_REGISTRY ? "" : sortHeader(headCell),
    align: headCell.align || "left",
    numeric: headCell.numeric,
    strong: headCell.id === InvestorsTableColumns.NAME,
    render: (value: React.ReactNode) => value,
  }));

  const renderIdentityRegistryAction = (row: IInvestorsListData["investors"][0]) => {
    const listing = listingByShareholderId[row._id];
    const showUnregister = Boolean(listing?.known && listing.registered);
    if (showUnregister) {
      return (
        <Button
          size="sm"
          variant="outline"
          data-testid={`investor-ir-unregister-${row._id}`}
          disabled={pendingRowId === row._id || !identityActionsReady}
          onClick={(event) => {
            event.stopPropagation();
            onUnregisterIdentity?.(row);
          }}
        >
          Unregister
        </Button>
      );
    }
    return (
      <Button
        size="sm"
        variant="outline"
        data-testid={`investor-ir-register-${row._id}`}
        disabled={pendingRowId === row._id || !identityActionsReady}
        onClick={(event) => {
          event.stopPropagation();
          onRegisterIdentity?.(row);
        }}
      >
        Register
      </Button>
    );
  };

  const rows: InvestorRow[] = loading
    ? []
    : (investorsList || []).map((row: IInvestorsListData["investors"][0]) => {
        const key = row._id;
        const contactName = personNameToString(
          row.contact?.firstName,
          row.contact?.secondName,
          row.contact?.thirdName,
          row.contact?.familyName
        );
        const investorWithHoldings = row.optionsCount || row.notesValue;
        const navUrl = investorWithHoldings
          ? `/company/${companyId}/register/investor-holdings/${key}`
          : `/company/${companyId}/investors/holdings/${key}`;

        return {
          key,
          legalEntityName: row.legalEntityName,
          contactName,
          sharesValue: row.sharesValue ? <Icon name="checkCircle" color="var(--success)" /> : "-",
          optionsCount: row.optionsCount ? <Icon name="checkCircle" color="var(--success)" /> : "-",
          notesValue: row.notesValue ? <Icon name="checkCircle" color="var(--success)" /> : "-",
          identityRegistry: showIdentityRegistryActions ? renderIdentityRegistryAction(row) : null,
          navUrl,
        };
      });

  if (loading) {
    const skeletonColumns = visibleHeadCells.map((headCell) => ({
      key: headCell.id,
      label: headCell.id === InvestorsTableColumns.IDENTITY_REGISTRY ? "" : sortHeader(headCell),
      align: headCell.align || "left",
      render: () => <Skeleton />,
    }));
    const skeletonRows = [{ key: "__loading" }];
    return <DataTable columns={skeletonColumns} rows={skeletonRows} rowKey="key" />;
  }

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey="key"
      onRowClick={(row: InvestorRow) => history.push(row.navUrl)}
      emptyText="No results"
    />
  );
};
